/**
 * Copywriting context from Account Brain (Notes & Files / dataVault).
 *
 * The vault is the long-term account store. Template KI-Kontext can pull
 * the facts that matter for outreach copy so users do not retype them.
 *
 * Skips header-notes (nav-only) and Ultimate Setup audit dumps.
 * Ultimate Setup aliases (brand, personas, goals) map onto the same groups.
 */

export const HEADER_NOTES_CATEGORY_ID = 'header-notes';

const COPYWRITING_CATEGORY_META = {
  business: { label: 'Business', priority: 0 },
  brand: { label: 'Brand & Voice', priority: 1 },
  audience: { label: 'Audience', priority: 10 },
  personas: { label: 'Personas', priority: 11 },
  painpoints: { label: 'Pain Points', priority: 12 },
  'selling-points': { label: 'Selling Points', priority: 20 },
  offer: { label: 'Offer', priority: 21 },
  'lead-magnet': { label: 'Lead Magnet', priority: 22 },
  proof: { label: 'Proof', priority: 30 },
  cta: { label: 'Call To Action', priority: 40 },
  general: { label: 'General', priority: 50 },
  goals: { label: 'Goals', priority: 51 },
};

const DEFAULT_MAX_CHARS = 6000;

function isSetupAuditNote(entry) {
  return !!(entry?.tags && entry.tags.setup === true && (entry.tags.raw === true || entry.tags.audit === true));
}

function hasSubstance(entry) {
  const title = typeof entry?.title === 'string' ? entry.title.trim() : '';
  const content = typeof entry?.content === 'string' ? entry.content.trim() : '';
  return content.length >= 12 || (title.length >= 3 && content.length >= 4);
}

/**
 * @param {Record<string, object>|null|undefined} entriesMap
 * @returns {Array<{ id: string, title: string, content: string, category: string, pinned: boolean, updatedAt: number }>}
 */
export function selectCopywritingVaultEntries(entriesMap) {
  if (!entriesMap || typeof entriesMap !== 'object') return [];

  const selected = [];
  for (const [id, entry] of Object.entries(entriesMap)) {
    if (!entry || typeof entry !== 'object') continue;
    if (entry.category === HEADER_NOTES_CATEGORY_ID) continue;
    if (isSetupAuditNote(entry)) continue;
    if (!(entry.category in COPYWRITING_CATEGORY_META)) continue;
    if (!hasSubstance(entry)) continue;
    selected.push({
      id,
      title: (entry.title || 'Note').trim(),
      content: String(entry.content || '').trim(),
      category: entry.category,
      pinned: !!entry.pinned,
      updatedAt: Number(entry.updatedAt) || 0,
    });
  }

  selected.sort((a, b) => {
    const pa = COPYWRITING_CATEGORY_META[a.category]?.priority ?? 99;
    const pb = COPYWRITING_CATEGORY_META[b.category]?.priority ?? 99;
    if (pa !== pb) return pa - pb;
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return (b.updatedAt || 0) - (a.updatedAt || 0);
  });

  return selected;
}

/**
 * @param {ReturnType<typeof selectCopywritingVaultEntries>} entries
 */
export function summarizeVaultCopyContext(entries) {
  const categories = [];
  const seen = new Set();
  for (const e of entries || []) {
    if (seen.has(e.category)) continue;
    seen.add(e.category);
    categories.push({
      id: e.category,
      label: COPYWRITING_CATEGORY_META[e.category]?.label || e.category,
    });
  }
  return { count: entries?.length || 0, categories };
}

/**
 * Structured prompt block for generateTemplateAI. Caps length so a full
 * vault cannot blow the system prompt.
 *
 * @param {ReturnType<typeof selectCopywritingVaultEntries>} entries
 * @param {{ maxChars?: number }} [opts]
 */
export function buildVaultCopyPromptBlock(entries, { maxChars = DEFAULT_MAX_CHARS } = {}) {
  if (!entries?.length) return '';

  const header = '=== ACCOUNT BRAIN (canonical account knowledge — treat as ground truth for facts about the sender, offer, audience, proof, and CTA. Do not invent facts that contradict this. Fields labelled "this template" below may add extras for this specific email.) ===';
  const parts = [];
  let used = 0;

  for (const e of entries) {
    const label = COPYWRITING_CATEGORY_META[e.category]?.label || e.category;
    const remaining = maxChars - used;
    if (remaining <= 80) break;

    let body = e.content;
    const prefix = `## ${label} — ${e.title}\n`;
    if (prefix.length + body.length > remaining) {
      const keep = remaining - prefix.length - 1;
      if (keep < 40) break;
      body = `${body.slice(0, keep).trimEnd()}…`;
    }
    const chunk = `${prefix}${body}`;
    parts.push(chunk);
    used += chunk.length + 2;
  }

  if (!parts.length) return '';
  return `${header}\n${parts.join('\n\n')}\n===`;
}
